import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Apollo } from 'apollo-angular';
import {
  CREATE_COPY,
  DELETE_COPY,
  GENERATE_COPY,
  GET_COPIES_LIST,
  GET_COPY,
  UPDATE_COPY,
} from '../graphql/copies.GraphQl';
import { Observable, map, takeWhile } from 'rxjs';
import { CopyBlockTranslationModel, CopyBlockModel, CopyModel } from '@models/copies/copies.model';
import { ulid } from 'ulid';
import { AutoCopyInput } from '@models/copies/copiesIA.model';
import { copiesChannelList } from '@shared/helpers/variables';
import { CurrentUserService } from '@core/services/currentUser.service';
import { PermissionType } from '@admin/modules/users/models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { ChipsTextbox, FormDropdown } from '@shared/components/dynamic-form/models';
import { localeList } from '@shared/helpers';

@Injectable({
  providedIn: 'root',
})
export class CopiesService {
  route = inject(ActivatedRoute);
  apollo = inject(Apollo);
  currentUserService = inject(CurrentUserService);
  translate = inject(TranslateService);

  searchCopies(): Observable<any> {
    return this.apollo
      .query({
        query: GET_COPIES_LIST,
        variables: {
          tenantId: this.route.snapshot.queryParams['tenant'],
        },
      })
      .pipe(map((res: any) => res.data.copies));
  }

  getCopy(id: string): Observable<any> {
    return this.apollo
      .query({
        query: GET_COPY,
        variables: {
          id,
        },
      })
      .pipe(map((res: any) => res));
  }

  deleteCopy(id: string): Observable<any> {
    return this.apollo
      .mutate({
        mutation: DELETE_COPY,
        variables: {
          id,
        },
      })
      .pipe(map((res: any) => res.data.deleteCopy));
  }

  updateCopy(copy: CopyModel): Promise<any> {
    const emitCopy = { ...copy };
    this.cleanCopyAttrs(emitCopy);
    return new Promise<any>((resolve) => {
      this.apollo
        .mutate({
          mutation: UPDATE_COPY,
          variables: {
            id: copy.id,
            input: emitCopy,
          },
        })
        .subscribe((res: any) => resolve(res));
    });
  }

  createCopy(input: any): Promise<any> {
    const { type, alias, templateId, name, blocks, includePaylink } = input;
    return new Promise<any>((resolve) => {
      this.apollo
        .mutate({
          mutation: CREATE_COPY,
          variables: {
            input: {
              id: ulid(),
              tenantId: this.route.snapshot.queryParams['tenant'],
              type,
              alias,
              templateId,
              name,
              isReminder: false,
              isPaymentAgreement: false,
              blocks,
              includePaylink,
            },
          },
        })
        .subscribe((res: any) => resolve(res));
    });
  }

  generateCopyBlocks(
    inputData: any,
    type: string,
  ): CopyBlockModel[] {
    let copyBlocks: CopyBlockModel[] = [];

    let fields =
      type === 'sms' || type === 'certified_sms'
        ? ['text']
        : ['subject', 'preHeader', 'text', 'button'];

    fields.forEach((field, fieldIndex) => {
      const check = () => {
        if (type === 'sms') {
          return 'smsText';
        }

        if (type === 'certified_sms') {
          return 'certifiedSmsText';
        }

        return field;
      };

      let copyBlock: CopyBlockModel = {
        translations: [],
        type: check(),
        alias: check(),
        order: fieldIndex + 1,
        maxCharacters: null,
      };

      inputData.forEach((data: any) => {
        copyBlock.translations.push({
          locale: data.value,
          text: data.content[field],
        });
      });

      copyBlocks.push(copyBlock);
    });

    return copyBlocks;
  }

  //UTILS
  getCoincidencies(
    copies: CopyModel[],
    namePartials?: string[],
    locales?: string[],
    types?: string[],
  ) {
    if (!copies) {
      return [];
    }

    return copies.filter(
      (copy) =>
        (!namePartials ||
          namePartials.length === 0 ||
          namePartials.some(
            (namePartial) =>
              !namePartial ||
              (copy.name && copy.name.toLowerCase().includes(namePartial.toLowerCase())),
          )) &&
        (!types || types.some((type) => copy.type === type)) &&
        (!locales ||
          locales.some(
            (locale) =>
              copy.blocks &&
              copy.blocks.some(
                (block: CopyBlockModel) =>
                  block.translations &&
                  block.translations.some(
                    (translation: CopyBlockTranslationModel) => translation.locale === locale,
                  ),
              ),
          )),
    );
  }

  getLanguages(blocks: CopyBlockModel[]): string[] {
    return Array.from(
      new Set(
        blocks.flatMap((block: CopyBlockModel) =>
          block.translations.map((translation: CopyBlockTranslationModel) => translation.locale),
        ),
      ),
    );
  }

  getName(
    name: keyof {
      email: string;
      sms: string;
      certified_sms: string;
      certified_email: string;
    },
  ) {
    const map = {
      email: 'DATA.ADMIN.MODULES.COPIES.EMAIL',
      sms: 'DATA.ADMIN.MODULES.COPIES.SMS',
      certified_sms: 'DATA.ADMIN.MODULES.COPIES.CERTIFIED_SMS',
      certified_email: 'DATA.ADMIN.MODULES.COPIES.CERTIFIED_EMAIL',
    };

    return this.translate.instant(map[name]);
  }

  private cleanCopyAttrs(copy: any) {
    delete copy.id;
    delete copy.tenantId;
    delete copy.type;
    delete copy.version;
    delete copy.createdAt;
    delete copy['__typename'];
    copy.blocks.forEach((block: any) => {
      delete block['__typename'];
      block.translations.forEach((translation: any) => {
        delete translation['__typename'];
      });
    });
  }

  checkArray(array: any) {
    for (let i = 0; i < array.length; i++) {
      let obj = array[i];

      for (let key in obj) {
        if (obj['channel'] === 'email' && key === 'pre-header') continue;

        if (obj['channel'] === 'sms' && key === 'text' && (!obj[key] || obj[key] === '')) {
          return false;
        }

        if (!obj[key] || obj[key] === '') {
          return false;
        }
      }
    }
    return true;
  }

  setCertifiedConfig(certifiedConfig: any, formConfig: any) {
    // Kept for backward compatibility; use setChannelAvailability instead.
  }

  setChannelAvailability(channelConfigs: any[], formConfig: any, preserveValue?: string) {
    const channelField = formConfig.find((item: any) => item.key === 'channel');
    if (!channelField || !Array.isArray(channelField.options)) return;

    const allowedChannels = new Set(
      (channelConfigs || [])
        .map((c: any) => (c?.channel ?? '').toString().toLowerCase())
        .filter(Boolean),
    );

    if (!allowedChannels.size) {
      return;
    }

    const keepOption = (opt: any) => {
      const value = (opt?.value ?? '').toString().toLowerCase();
      return allowedChannels.has(value) || (!!preserveValue && value === preserveValue.toLowerCase());
    };

    channelField.options = channelField.options.filter(keepOption);

    if (preserveValue && !channelField.options.some((o: any) => o.value === preserveValue)) {
      const fallback = copiesChannelList.find((o: any) => o.value === preserveValue);
      if (fallback) {
        channelField.options.push({
          value: fallback.value,
          label: fallback.label,
        });
      }
    }

    if (
      channelField.value &&
      !channelField.options.some((o: any) => o.value === channelField.value)
    ) {
      channelField.value = channelField.options[0]?.value ?? channelField.value;
    }
  }

  checkValues(items: any, channel: string) {
    let result = true;
    items.forEach((item: any) => {
      if (channel === 'certified_sms') {
        if (!item.content || !item.content.text) {
          result = false;
          return;
        }
      } else if (channel === 'sms') {
        if (!item.content || !item.content.text) {
          result = false;
          return;
        }
      } else if (channel === 'email' || channel === 'certified_email') {
        Object.keys(item.content).forEach((key) => {
          if (key !== 'preHeader' && (item.content[key] === '' || item.content[key] === null)) {
            result = false;
            return;
          }
        });
      } else {
        result = false;
      }
    });
    return result;
  }

  checkValue(item: any, channel: string) {
    let result = true;
    if (channel === 'certified_sms') {
      if (!item.content || !item.content.text) {
        result = false;
      }
    } else if (channel === 'sms') {
      if (!item.content || !item.content.text) {
        result = false;
      }
    } else if (channel === 'email' || channel === 'certified_email') {
      if (item.content) {
        Object.keys(item.content).forEach((key) => {
          if (key !== 'preHeader' && (item.content[key] === '' || item.content[key] === null)) {
            result = false;
          }
        });
      } else {
        result = false;
      }
    } else {
      result = false;
    }
    return result;
  }

  canWrite() {
    return this.currentUserService.isPermissionAuthorized([
      PermissionType.FULL_ACCESS,
      PermissionType.COPY_WRITE,
    ]);
  }
}
